import { useEffect, useMemo, useRef, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Coins, ShieldCheck, Lock, FileText, Download, TrendingUp, ArrowDownUp, Sparkles, Map, Rocket, CheckCircle2, Circle } from "lucide-react";
import LiveMiningCard from "@/components/LiveMiningCard";
import LiveCandleChart from "@/components/LiveCandleChart";

// Vision 2030 roadmap (finalized with user's official copy)
const ROADMAP = [
  { year: "2027", phase: "🌱 Foundation", subtitle: "Building today for tomorrow", status: "done", color: "#22C55E", items: ["Wallet", "Bonus", "18M Lock", "Reward", "Live Chart"] },
  { year: "2028", phase: "⚡ Utility", subtitle: "Real use · real value", status: "next", color: "#3B82F6", items: ["Buy", "Platform Use", "Partner Ecosystem"] },
  { year: "2029", phase: "🌍 Expansion", subtitle: "Bigger reach · stronger network", status: "planned", color: "#A855F7", items: ["Wider Ecosystem", "External Wallet", "Security"] },
  { year: "2030", phase: "🏆 Global Vision", subtitle: "A token for a brighter tomorrow", status: "planned", color: "#EAB308", items: ["Mass Utility", "Market Readiness", "Global Ecosystem"] },
];

export default function NBToken() {
  const [wallet, setWallet] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [txs, setTxs] = useState([]);
  const [certs, setCerts] = useState([]);
  const [tab, setTab] = useState("overview");
  const [tradeOpen, setTradeOpen] = useState(null);
  const [liveInfo, setLiveInfo] = useState(null);
  const [livePrice, setLivePrice] = useState(0);
  const [priceDir, setPriceDir] = useState(0); // -1 down, 0 neutral, 1 up
  const [tfKey, setTfKey] = useState("1m");
  const ticksRef = useRef([]);
  const [candles, setCandles] = useState([]);
  const [tickBalance, setTickBalance] = useState(0);

  const TIMEFRAMES = useMemo(() => ({
    "1m":  { ms: 60 * 1000,               label: "1m",  synthCount: 60 },
    "3m":  { ms: 3 * 60 * 1000,           label: "3m",  synthCount: 80 },
    "5m":  { ms: 5 * 60 * 1000,           label: "5m",  synthCount: 80 },
    "15m": { ms: 15 * 60 * 1000,          label: "15m", synthCount: 80 },
    "30m": { ms: 30 * 60 * 1000,          label: "30m", synthCount: 80 },
    "1h":  { ms: 60 * 60 * 1000,          label: "1H",  synthCount: 80 },
    "4h":  { ms: 4 * 60 * 60 * 1000,      label: "4H",  synthCount: 90 },
    "1d":  { ms: 24 * 60 * 60 * 1000,     label: "1D",  synthCount: 60 },
    "7d":  { ms: 7 * 24 * 60 * 60 * 1000, label: "1W",  synthCount: 30 },
    "30d": { ms: 30 * 24 * 60 * 60 * 1000, label: "1M", synthCount: 12 },
  }), []);

  const load = async () => {
    try {
      const [w, h, ce, lp, cf] = await Promise.all([
        api.get("/nb-token/wallet"),
        api.get("/nb-token/history"),
        api.get("/nb-token/certificates"),
        api.get("/nb-token/live-price"),
        api.get("/nb-token/config"),
      ]);
      setWallet(w.data);
      setTxs(h.data?.transactions || []);
      setCerts(ce.data?.certificates || []);
      setLiveInfo(lp.data);
      setCfg(cf.data);
      setLivePrice(Number(lp.data?.base_price || 0));
      setTickBalance(Number(w.data?.balance_total || 0));
    } catch {
      /* ignore */
    }
  };
  useEffect(() => {
    load();
  }, []);

  // Live 3-second random-walk price ticker within market variance
  useEffect(() => {
    if (!liveInfo) return;
    const base = Number(liveInfo.base_price);
    const vpct = Number(liveInfo.variance_pct) / 100;
    const iv = setInterval(() => {
      setLivePrice((p) => {
        const bounded = Math.min(base * (1 + vpct), Math.max(base * (1 - vpct), p));
        // Random walk step: -0.6% to +0.6% of base, then clamp to bounds
        const step = (Math.random() - 0.5) * 2 * base * (vpct / 3);
        let np = bounded + step;
        np = Math.min(base * (1 + vpct), Math.max(base * (1 - vpct), np));
        setPriceDir(np > p ? 1 : np < p ? -1 : 0);
        const ts = Date.now();
        ticksRef.current.push({ t: ts, p: np });
        if (ticksRef.current.length > 2000) ticksRef.current.splice(0, ticksRef.current.length - 2000);
        return np;
      });
    }, Number(liveInfo.tick_interval_sec || 3) * 1000);
    return () => clearInterval(iv);
  }, [liveInfo]);

  // Build OHLC candles: seed synthetic history for the selected TF then overlay live ticks
  useEffect(() => {
    if (!liveInfo) return;
    const base = Number(liveInfo.base_price);
    const vpct = Number(liveInfo.variance_pct) / 100;
    const tf = TIMEFRAMES[tfKey];
    const build = () => {
      const bucketMs = tf.ms;
      // Try localStorage for stable synthetic history per TF
      const key = `nbt_candles_${tfKey}_v2`;
      let synth = null;
      try {
        const raw = localStorage.getItem(key);
        if (raw) synth = JSON.parse(raw);
      } catch {}
      if (!synth || !Array.isArray(synth) || synth.length !== tf.synthCount) {
        // Generate synthetic candles anchored to base price
        synth = [];
        let prev = base * (1 - vpct * 0.3 * Math.random());
        const now = Date.now();
        const startBucket = Math.floor(now / bucketMs) * bucketMs - (tf.synthCount - 1) * bucketMs;
        for (let i = 0; i < tf.synthCount; i++) {
          const o = prev;
          let h = o, l = o, c = o;
          const walkSteps = 12;
          for (let j = 0; j < walkSteps; j++) {
            const step = (Math.random() - 0.5) * base * vpct * 0.35;
            c = Math.min(base * (1 + vpct * 1.15), Math.max(base * (1 - vpct * 1.15), c + step));
            if (c > h) h = c;
            if (c < l) l = c;
          }
          synth.push({ t: startBucket + i * bucketMs, o, h, l, c });
          prev = c;
        }
        try { localStorage.setItem(key, JSON.stringify(synth)); } catch {}
      }
      // Overlay live ticks into their bucket (updates the most recent candle only)
      const arr = synth.map((k) => ({ ...k }));
      const nowBucket = Math.floor(Date.now() / bucketMs) * bucketMs;
      const liveBucket = arr.find((k) => k.t === nowBucket) || arr[arr.length - 1];
      const liveTicks = ticksRef.current.filter((tk) => tk.t >= liveBucket.t);
      if (liveTicks.length) {
        liveBucket.o = liveBucket.o;
        for (const tk of liveTicks) {
          liveBucket.c = tk.p;
          if (tk.p > liveBucket.h) liveBucket.h = tk.p;
          if (tk.p < liveBucket.l) liveBucket.l = tk.p;
        }
      }
      // Attach the price range as an array so recharts renders body-only bars; wick is drawn in shape
      const withRange = arr.map((k) => ({ ...k, range: [k.l, k.h] }));
      setCandles(withRange);
    };
    build();
    const iv = setInterval(build, 1500);
    return () => clearInterval(iv);
  }, [tfKey, liveInfo, TIMEFRAMES]);

  // Live mining balance ticker — increments locally based on daily_mining_pct
  useEffect(() => {
    if (!wallet || !cfg?.mining_enabled) return;
    const activeInv = Number(cfg?.daily_mining_pct || 0);
    // per-second NBT added = (active_investment_usdt × pct/100 / 86400) / price
    // We don't have active_investment here; instead assume mining pace = 0.001% of total_balance/sec if enabled
    const perSecond = Number(wallet.balance_total || 0) * (activeInv / 100) / 86400;
    if (perSecond <= 0) return;
    const iv = setInterval(() => {
      setTickBalance((b) => b + perSecond);
    }, 1000);
    return () => clearInterval(iv);
  }, [wallet, cfg]);

  const balance = Number(tickBalance || wallet?.balance_total || 0);
  const locked = Number(wallet?.balance_locked || 0);
  const unlocked = Number(wallet?.balance_unlocked || 0);
  const price = livePrice || Number(wallet?.current_price || 0);
  const value = balance * price;
  const symbol = wallet?.symbol || "NBT";
  const name = wallet?.name || "NextBillion Token";
  const logo = wallet?.logo_url || "/icons/nb-token-logo.jpg";
  const lockMonths = Number(wallet?.lock_months || 18);
  const buyEnabled = !!cfg?.buy_enabled;
  const sellEnabled = !!cfg?.sell_enabled;

  const openCertPdf = (cert) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/nb-token/certificate/${cert.id}/pdf`;
    const token = localStorage.getItem("nb_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => window.open(window.URL.createObjectURL(b), "_blank"));
  };

  return (
    <AppLayout variant="user">
      {/* HERO — mobile-friendly stacked layout */}
      <div
        className="nb-card p-5 md:p-8 relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, rgba(234,179,8,0.18) 0%, rgba(15,22,38,1) 55%)",
          borderColor: "rgba(234,179,8,0.35)",
        }}
      >
        <div className="absolute -right-14 -top-14 h-48 w-48 rounded-full bg-[#EAB308]/15 blur-3xl" />
        <div className="flex items-center gap-4 flex-wrap">
          <img
            src={logo}
            alt="NBT"
            className="h-16 w-16 md:h-20 md:w-20 rounded-2xl ring-2 ring-[#EAB308]/40 shrink-0"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#EAB308]">Digital Asset Ecosystem</div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl md:text-4xl text-white mt-1 leading-tight">
              {name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-[#EAB308] font-mono text-lg">{symbol}</span>
              <span className={`inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded border ${priceDir >= 0 ? "border-[#8AE234]/40 text-[#8AE234] bg-[#8AE234]/5" : "border-red-500/40 text-red-400 bg-red-500/5"}`}>
                <span className={`h-1.5 w-1.5 rounded-full ${priceDir >= 0 ? "bg-[#8AE234]" : "bg-red-500"} animate-pulse`}></span>
                ${price.toFixed(4)}
              </span>
            </div>
          </div>
        </div>
        <p className="text-slate-400 text-xs md:text-sm mt-4 leading-relaxed">
          Promotional utility token with a {lockMonths}-month lock. Fully isolated from your Wallet Balance — cannot be withdrawn as USDT.
        </p>
        <div className="flex gap-2 mt-4 flex-wrap">
          <button data-testid="nb-btn-buy" disabled={!buyEnabled} onClick={() => setTradeOpen("buy")} className={`px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 border ${buyEnabled ? "bg-[#8AE234]/10 border-[#8AE234]/40 text-[#8AE234] hover:bg-[#8AE234]/20" : "border-white/10 text-slate-500 cursor-not-allowed"}`}>
            <ArrowDownUp size={12}/> Buy {symbol}
          </button>
          <button data-testid="nb-btn-sell" disabled={!sellEnabled || unlocked <= 0} onClick={() => setTradeOpen("sell")} className={`px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 border ${(sellEnabled && unlocked > 0) ? "bg-red-500/10 border-red-500/40 text-red-400 hover:bg-red-500/20" : "border-white/10 text-slate-500 cursor-not-allowed"}`}>
            <ArrowDownUp size={12}/> Sell {symbol}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Total Balance" value={`${balance.toFixed(4)}`} sub={`${symbol} · ≈ $${value.toFixed(2)}`} color="#EAB308" icon={Coins} pulse={!!cfg?.mining_enabled}/>
        <KPI label="Locked" value={`${locked.toFixed(4)}`} sub={`${lockMonths}-mo lock`} color="#F97316" icon={Lock} />
        <KPI label="Unlocked" value={`${unlocked.toFixed(4)}`} sub="Available to sell" color="#22C55E" icon={ShieldCheck} />
        <KPI label="Live Price" value={`$${price.toFixed(4)}`} sub={priceDir >= 0 ? "↑ real-time" : "↓ real-time"} color={priceDir >= 0 ? "#22C55E" : "#EF4444"} icon={TrendingUp} />
      </div>

      {/* Live Mining Card — full-width row */}
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <LiveMiningCard wallet={wallet} cfg={cfg}/>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-2 text-xs uppercase tracking-widest flex-wrap">
        {[
          { k: "overview", l: "Overview" },
          { k: "chart", l: "Live Chart" },
          { k: "history", l: `History (${txs.length})` },
          { k: "certificates", l: `Certificates (${certs.length})` },
          { k: "roadmap", l: "Roadmap" },
        ].map((t) => (
          <button key={t.k} data-testid={`nb-tab-${t.k}`} onClick={() => setTab(t.k)} className={`px-3 py-2 rounded-lg border transition-colors ${tab === t.k ? "bg-[#EAB308]/10 border-[#EAB308]/40 text-[#EAB308]" : "border-white/10 text-slate-400 hover:text-white"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <InfoCard title="What is NBT?">NextBillion Token (NBT) is a promotional utility asset issued by the platform. Every qualifying deposit and every buy earns you an NBT allocation locked for {lockMonths} months. Certificates prove ownership.</InfoCard>
          <InfoCard title="Wallet Isolation">NBT balance never mixes with your USDT Wallet Balance or Credit Wallet. Only <span className="text-[#22C55E] font-mono">unlocked</span> NBT (matured allocations) can be sold back to USDT.</InfoCard>
          <InfoCard title="Live Mining">{cfg?.mining_enabled ? <>Your balance is auto-mining right now — watch it grow live in the top-left KPI.</> : "Mining rewards are paused during system upgrades — coming back soon."}</InfoCard>
          <InfoCard title="Certificate on Every Buy">Every purchase generates a new bond-style certificate PDF. Download them anytime from the Certificates tab — perfect for record-keeping.</InfoCard>
        </div>
      )}

      {tab === "chart" && (
        <div className="nb-card p-4 md:p-6 mt-4" style={{ borderColor: "rgba(234,179,8,0.35)" }}>
          <LiveCandleChart liveInfo={liveInfo} cfg={cfg}/>
        </div>
      )}

      {tab === "history" && (
        <div className="nb-card p-4 md:p-6 mt-4">
          {txs.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No NBT activity yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500 uppercase tracking-widest">
                  <tr className="border-b border-white/5">
                    <th className="text-left py-2">Date</th>
                    <th className="text-left">Type</th>
                    <th className="text-right">Amount</th>
                    <th className="text-right">Price</th>
                    <th className="text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {txs.map((t) => (
                    <tr key={t.id} className="border-b border-white/5">
                      <td className="py-2 text-slate-400 font-mono">{String(t.created_at || "").slice(0, 19).replace("T", " ")}</td>
                      <td>
                        <span className={`uppercase text-[10px] px-2 py-0.5 rounded ${
                          t.type === "buy" ? "bg-[#8AE234]/10 text-[#8AE234]" :
                          t.type === "sell" ? "bg-red-500/10 text-red-400" :
                          t.type === "unlock" ? "bg-blue-500/10 text-blue-400" :
                          t.type === "mining" ? "bg-purple-500/10 text-purple-400" :
                          t.type === "referrer_bonus" ? "bg-cyan-500/10 text-cyan-400" :
                          "bg-[#EAB308]/10 text-[#EAB308]"
                        }`}>{t.type}</span>
                      </td>
                      <td className="text-right font-mono text-white">{Number(t.amount_nb || 0).toFixed(4)}</td>
                      <td className="text-right font-mono text-slate-300">${Number(t.price_usdt || 0).toFixed(4)}</td>
                      <td className="text-right font-mono text-[#8AE234]">${Number(t.value_usdt || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "certificates" && (
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          {certs.length === 0 ? (
            <div className="nb-card p-6 text-sm text-slate-500 text-center col-span-full">No certificates issued yet.</div>
          ) : (
            certs.map((c) => (
              <div key={c.id} className="nb-card p-5" style={{ borderColor: "rgba(234,179,8,0.35)" }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#EAB308]">{c.source_type === "buy" ? "Purchase Certificate" : "Bond Certificate"}</div>
                    <div className="font-heading text-white text-lg mt-1">{c.cert_no}</div>
                  </div>
                  <FileText size={22} className="text-[#EAB308]" />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                  <div><div className="text-slate-500 uppercase tracking-widest">NBT</div><div className="font-mono text-white">{Number(c.amount_nb).toFixed(4)}</div></div>
                  <div><div className="text-slate-500 uppercase tracking-widest">Value</div><div className="font-mono text-[#8AE234]">${Number(c.value_usdt).toFixed(2)}</div></div>
                  <div><div className="text-slate-500 uppercase tracking-widest">Issued</div><div className="font-mono text-slate-300">{String(c.issued_at || "").slice(0, 10)}</div></div>
                  <div><div className="text-slate-500 uppercase tracking-widest">Lock ends</div><div className="font-mono text-slate-300">{String(c.lock_until || "").slice(0, 10)}</div></div>
                </div>
                <button onClick={() => openCertPdf(c)} className="mt-4 nb-btn-primary text-xs w-full flex items-center justify-center gap-2">
                  <Download size={12} /> Download PDF
                </button>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "roadmap" && (
        <div className="mt-4">
          <div className="text-center mb-6">
            <div className="text-xs uppercase tracking-widest text-[#EAB308] flex items-center justify-center gap-2"><Rocket size={12}/> NB Token · Vision 2030</div>
            <h3 className="font-heading font-bold text-2xl md:text-3xl text-white mt-2">More than a token — a global movement</h3>
            <p className="text-slate-400 text-sm mt-2 max-w-lg mx-auto">A stronger tomorrow for a brighter world</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {ROADMAP.map((r, i) => (
              <div key={i} className="nb-card p-5 relative overflow-hidden" style={{ borderColor: `${r.color}66` }}>
                <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full blur-2xl" style={{ background: `${r.color}22` }}/>
                <div className="relative">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-3xl font-bold" style={{ color: r.color }}>{r.year}</div>
                    {r.status === "done" ? <CheckCircle2 size={18} style={{ color: r.color }}/> : r.status === "next" ? <Rocket size={18} style={{ color: r.color }}/> : <Circle size={18} className="text-slate-500"/>}
                  </div>
                  <div className="font-heading text-white text-lg mt-2">{r.phase}</div>
                  <div className="text-[11px] text-slate-400 uppercase tracking-widest mt-1">{r.subtitle}</div>
                  <ul className="mt-3 space-y-1.5 text-sm text-slate-300">
                    {r.items.map((it, j) => (<li key={j} className="flex items-center gap-2"><Sparkles size={10} style={{ color: r.color }} className="shrink-0"/> {it}</li>))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 nb-card p-4 text-center" style={{ borderColor: "rgba(234,179,8,0.25)" }}>
            <div className="font-heading text-white text-lg flex items-center justify-center gap-3 flex-wrap">
              <span className="text-[#22C55E]">BUILD</span> →
              <span className="text-[#3B82F6]">UTILIZE</span> →
              <span className="text-[#A855F7]">EXPAND</span> →
              <span className="text-[#EAB308]">GROW</span> 🌐
            </div>
            <p className="text-xs text-slate-400 mt-2">Building a more connected, empowered and inclusive tomorrow.</p>
          </div>
          <div className="text-center mt-4 text-[10px] text-slate-500 italic">Roadmap subject to technology, regulatory and market conditions.</div>
        </div>
      )}

      {tradeOpen && <TradeModal mode={tradeOpen} symbol={symbol} price={price} unlocked={unlocked} onClose={() => setTradeOpen(null)} onDone={load} cfg={cfg}/>}
    </AppLayout>
  );
}

function Candle(props) {
  const { x, y, width, height, payload } = props;
  if (!payload) return null;
  const bullish = payload.c >= payload.o;
  const color = bullish ? "#22C55E" : "#EF4444";
  const range = payload.h - payload.l;
  if (range <= 0) {
    // Doji — draw a flat line
    return <line x1={x - 2} x2={x + width + 2} y1={y + height / 2} y2={y + height / 2} stroke={color} strokeWidth={1}/>;
  }
  const pxPerUnit = height / range;
  const bodyTopVal = Math.max(payload.o, payload.c);
  const bodyBotVal = Math.min(payload.o, payload.c);
  const bodyY = y + (payload.h - bodyTopVal) * pxPerUnit;
  const bodyH = Math.max(1, (bodyTopVal - bodyBotVal) * pxPerUnit);
  const wickX = x + width / 2;
  const bodyW = Math.max(3, width * 1.6);
  return (
    <g>
      <line x1={wickX} x2={wickX} y1={y} y2={y + height} stroke={color} strokeWidth={1}/>
      <rect x={wickX - bodyW / 2} y={bodyY} width={bodyW} height={bodyH} fill={bullish ? "transparent" : color} stroke={color} strokeWidth={1}/>
    </g>
  );
}

function KPI({ label, value, sub, color, icon: Icon, pulse }) {
  return (
    <div className="nb-card p-5 relative overflow-hidden">
      {pulse && <div className="absolute top-2 right-2 h-1.5 w-1.5 rounded-full bg-[#EAB308] animate-pulse" title="Live mining"/>}
      <div className="flex items-center justify-between text-xs uppercase tracking-widest text-slate-400">
        <span>{label}</span>{Icon && <Icon size={14} style={{ color }} />}
      </div>
      <div className="mt-2 font-mono text-xl md:text-2xl font-bold" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div className="nb-card p-5">
      <div className="font-heading text-white font-semibold text-sm">{title}</div>
      <p className="text-slate-400 text-xs md:text-sm mt-2 leading-relaxed">{children}</p>
    </div>
  );
}

function TradeModal({ mode, symbol, price, unlocked, onClose, onDone, cfg }) {
  const isBuy = mode === "buy";
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const num = Number(amount) || 0;
  const nbtOut = isBuy ? (price > 0 ? num / price : 0) : num;
  const usdtOut = isBuy ? num : num * price;
  const minBuy = Number(cfg?.min_buy_usdt || 10);
  const maxBuy = Number(cfg?.max_buy_usdt || 10000);
  const submit = async () => {
    setBusy(true);
    try {
      if (isBuy) {
        const { data } = await api.post("/nb-token/buy", { usdt_amount: num });
        toast.success(`Bought ${data.amount_nb.toFixed(4)} ${symbol} · certificate generated`);
      } else {
        const { data } = await api.post("/nb-token/sell", { nbt_amount: num });
        toast.success(`Sold ${data.amount_nb.toFixed(4)} ${symbol} for $${data.usdt_received.toFixed(2)}`);
      }
      onDone(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trade failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="nb-card p-6 w-full max-w-md" style={{ borderColor: "rgba(234,179,8,0.35)" }}>
        <div className="font-heading text-white text-lg mb-2">{isBuy ? "Buy" : "Sell"} {symbol}</div>
        <div className="text-xs text-slate-400 mb-4">Live price: <span className="text-[#EAB308] font-mono">${price.toFixed(4)}</span></div>
        <label className="block">
          <div className="text-xs text-slate-400 mb-1">{isBuy ? "USDT to spend" : `${symbol} to sell (unlocked only)`}</div>
          <input data-testid={`nb-${mode}-amount`} type="number" step="0.0001" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={isBuy ? `Min $${minBuy}` : `Available ${unlocked.toFixed(4)}`} className="w-full bg-[#070B14] border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-[#EAB308]/40 outline-none"/>
        </label>
        {isBuy && <div className="text-[11px] text-slate-500 mt-1">Min ${minBuy} · Max ${maxBuy} · Purchase will be {cfg?.lock_purchases ? `LOCKED for ${cfg?.lock_months || 18} months` : "unlocked immediately"} · certificate auto-generated</div>}
        {!isBuy && <div className="text-[11px] text-slate-500 mt-1">Unlocked available: {unlocked.toFixed(4)} {symbol}</div>}
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
          <div className="bg-[#070B14] border border-white/5 rounded-lg p-3">
            <div className="text-slate-500 uppercase tracking-widest">You get</div>
            <div className="font-mono text-white mt-1">{isBuy ? `${nbtOut.toFixed(4)} ${symbol}` : `$${usdtOut.toFixed(2)}`}</div>
          </div>
          <div className="bg-[#070B14] border border-white/5 rounded-lg p-3">
            <div className="text-slate-500 uppercase tracking-widest">Rate</div>
            <div className="font-mono text-slate-300 mt-1">${price.toFixed(4)}/{symbol}</div>
          </div>
        </div>
        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 border border-white/10 rounded-lg py-2 text-sm text-slate-300 hover:bg-white/5">Cancel</button>
          <button data-testid={`nb-${mode}-submit`} onClick={submit} disabled={busy || !num} className="flex-1 nb-btn-primary text-sm">{busy ? "Processing…" : (isBuy ? `Buy ${symbol}` : `Sell ${symbol}`)}</button>
        </div>
      </div>
    </div>
  );
}
